import React, { useRef, useEffect, useMemo, useState, useCallback } from 'react';
import merge from 'lodash.merge';
//import { debounce } from 'lodash';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useTranslation } from 'react-i18next';

import { sqlDateToLabel } from 'helpers/helpers'; //, compareISODates
import { assembleHistoricalEvents, offsetLabel, devGraphLineColor } from 'pages/Accounts/Edit/Plan/Chart/helpers';
import { AmountValue } from 'common/interfaces';
import { Currency } from 'enums';
import { DevelopmentGraphData } from 'components/Graph/DevelopmentLineGraph';

const xAxisLabelFormatter = function (this: Highcharts.AxisLabelsFormatterContextObject<number>) {
  const value = `${this.value}`;
  return `${sqlDateToLabel(value, 'dd.MM.yyyy')}`;
};

interface ValueData {
  value: AmountValue;
  date: string;
}

interface Props {
  data: DevelopmentGraphData;
  settings?: Highcharts.Options;
  currency: Currency;
}

const DevelopmentLineGraph: React.FC<Props> = ({ data, settings, currency }) => {
  const {
    i18n: { language },
    t,
  } = useTranslation();
  const portfolio = 'total';
  const chartRef = useRef<Highcharts.Chart | null>();
  const domRef = useRef<HTMLDivElement | null>(null);
  const currentPortfolio = data.investments[portfolio];
  const transfersData = useMemo(
    () => (currentPortfolio ? currentPortfolio.map((dp: ValueData) => [dp.date, dp.value.value]) : []),
    [currentPortfolio],
  );
  const [chart, setChart] = useState<Highcharts.Chart | undefined | null>(undefined);

  useEffect(() => {
    setChart(chartRef.current);
  }, [setChart]);

  const events = assembleHistoricalEvents(
    data.targets.total || [],
    data.withdrawals.total || [],
    data.singleDeposits.total || [],
    data.periodicDeposits.total || [],
    data.investments.total!,
    currency,
    '_hover',
    false,
    [],
    true,
  );

  const setupLabels = useCallback(
    (visible: boolean, useShiftedTotals: boolean) => {
      const { current: chart } = chartRef;
      const yMin = chart?.yAxis[0].min || 0;
      const yMax = chart?.yAxis[0].max || 1;
      const xMax = chart?.xAxis[0].max || 1;
      const chartAreaWidth = chart?.plotWidth || 1;
      const chartAreaHeight = chart?.plotHeight || 1;
      const chartStartX = chart?.plotLeft || 0;
      const chartStartY = chart?.plotTop || 0;
      const { current: dom } = domRef;
      const labels = dom ? Array.from(dom.getElementsByClassName('highcharts-plot-line-label')) : [];
      const plotLines = dom ? Array.from(dom.getElementsByClassName('custom-plot-line')) : [];

      const tickPx = chartAreaWidth / (xMax || 1);
      const chartSpacingTop = chart?.options?.chart?.spacingTop || 0;

      let prevSize: number[] = [],
        prevShift: number[] = [],
        prevPos: number[] = [];
      for (let i = 0; i < labels.length; i++) {
        offsetLabel(
          labels[i],
          plotLines[i],
          true,
          yMin,
          yMax,
          chartAreaHeight,
          chartSpacingTop,
          chartStartX,
          chartStartY,
          [],
          tickPx,
          prevSize,
          prevShift,
          prevPos,
        );
      }
    },
    [domRef],
  );

  const lineColor = devGraphLineColor(portfolio);
  const convertedData = data.values[portfolio]!.map((dp: ValueData) => [dp.date, dp.value.value]);
  const seriesData = [
    {
      id: 'transfers',
      name: 'transfers',
      data: transfersData,
      color: '#666',
      fillOpacity: 1,
      fillColor: '#f5f5f5',
    },
    {
      id: 'portfolioValues',
      name: 'portfolioValues',
      data: convertedData,
      color: lineColor,
    },
  ];

  useEffect(() => {
    if (chart) {
      const values: number[] = [];
      if (chart.series[0]) {
        chart.series[0].points.forEach((point, i) => {
          point.y !== undefined && point.y !== null && values.push(point.y);
        });
      }
      chart.series[1] &&
        chart.series[1].points.forEach((point) => {
          point.y !== undefined && point.y !== null && values.push(point.y);
        });
      if (values.length) {
        const min = Math.min(...values);
        const max = Math.max(...values);
        values.length && chart.yAxis[0].setExtremes(min, max + (max - min) * 0.1);
      }
      const min = chart.xAxis[0].min || 0;
      const max = chart.xAxis[0].max || 0;
      const interval = 5;
      chart.xAxis[0].update({ tickInterval: Math.floor((max - min) / interval) });
    }
  }, [chart]);

  const defaultSettings: Highcharts.Options = {
    title: { text: `` },
    chart: {
      width: 708,
      height: 240,
      animation: false,
      type: 'area',
      spacingRight: 40,
      marginRight: 40,
      events: {
        redraw: function (this) {
          setupLabels(true, false);
        },
      },
    },
    credits: {
      enabled: false,
    },
    xAxis: {
      labels: {
        enabled: true,
        formatter: xAxisLabelFormatter,
        overflow: 'justify',
        reserveSpace: true,
      },
      type: 'category',
      plotLines: events,
    },
    yAxis: {
      title: {
        text: '',
      },
      labels: {
        formatter: function (this: Highcharts.AxisLabelsFormatterContextObject<number>) {
          return `${this.value.toLocaleString(language)} ${t('currency_' + currency)}`;
        },
      },
    },
    plotOptions: {
      area: {
        lineWidth: 2.5,
        fillOpacity: 0,
        marker: {
          symbol: 'circle',
          radius: 8,
          enabled: false,
        },
      },
      series: {
        enableMouseTracking: false,
        animation: false,
        marker: { enabled: false },
      },
    },
    series: seriesData as any,
    legend: {
      align: 'right',
      layout: 'horizontal',
      verticalAlign: 'bottom',
      floating: false,
      symbolWidth: 16,
      symbolHeight: 8,
      symbolRadius: 0,
      squareSymbol: false,
      useHTML: true,
      itemStyle: { lineHeight: '17px' },
      itemMarginTop: 12,
    },
  };

  Highcharts.setOptions({
    lang: {
      numericSymbols: [' tis.', ' mil.', ' mld.'],
    },
  });


  const graphSettings = settings ? merge({}, defaultSettings, settings) : defaultSettings;
  return (
    <div ref={domRef}>
      <HighchartsReact
        highcharts={Highcharts}
        options={graphSettings}
        callback={(chart: any) => {
          chartRef.current = chart;
        }}
      />
    </div>
  );
};

export default React.memo(DevelopmentLineGraph);
